import { Grid, Typography, Box, Divider, MenuItem, Select, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { StyledInput, Label, grey, GlassCard, SELECTED_MENU_PROPS } from '../../StyledComponents/StyledComponent';
import PersonIcon from '@mui/icons-material/Person';
import ShareIcon from '@mui/icons-material/Share';

const SectionTitle = styled(Typography)(() => ({
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
}));

const CustomSelect = styled(({ MenuProps, ...props }) => (
    <Select MenuProps={{ ...SELECTED_MENU_PROPS, ...MenuProps }} {...props} />
))(({ theme }) => ({
    width: '100%',
    height: '40px',
    backgroundColor: theme.palette.mode === 'dark' ? alpha(grey[900], 0.4) : alpha('#fff', 0.6),
    backdropFilter: 'blur(8px)',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.mode === 'dark' ? alpha(grey[700], 0.5) : alpha(grey[200], 0.5)}`,
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    boxShadow: `0 4px 6px ${alpha('#000', 0.02)}`,
    transition: 'all 0.2s ease',
    '&:hover': {
        border: '1px solid #3DAC5A',
    },
    '&.Mui-focused': {
        border: '1px solid #3DAC5A',
        backgroundColor: '#fff',
    }
}));

const PersonalTab = ({ formData, handleInputChange, handleDateChange, masterData }) => {
    const { locations = [] } = masterData || {};

    // Helper to ensure the current value is always an option (prevents MUI
    // warnings). A legacy value that doesn't match any master-list entry is
    // surfaced right after the placeholder — not buried at the bottom — with a
    // disabled hint line explaining why. The hint is its OWN MenuItem, never
    // the selectable one: MUI's Select renders the selected MenuItem's
    // children verbatim in the closed box, so if the explanation lived inside
    // the selectable item itself, it would leak into the field's display text.
    const renderSafeOptions = (options, currentValue, placeholder) => {
        const hasValue = options.some(opt => (opt.name || opt) === currentValue);
        return [
            <MenuItem key="placeholder" value="" disabled>{placeholder}</MenuItem>,
            currentValue && !hasValue && (
                <MenuItem key="fallback-hint" disabled sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                    Current value (not in master list):
                </MenuItem>
            ),
            currentValue && !hasValue && (
                <MenuItem key="fallback" value={currentValue}>{currentValue}</MenuItem>
            ),
            ...options.map((opt, i) => (
                <MenuItem key={i} value={opt.name || opt}>{opt.name || opt}</MenuItem>
            )),
        ];
    };
    return (
        <GlassCard sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
                <SectionTitle sx={{ mb: 1 }}>
                    <PersonIcon sx={{ color: '#3DAC5A' }} />
                    Personal Information
                </SectionTitle>
                <Typography variant="body2" color="text.secondary">
                    Update employee's basic personal details and social profiles.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <Label>Employee ID</Label>
                    <StyledInput
                        name="employee_no"
                        value={formData.employee_no || ''}
                        disabled
                        inputProps={{ readOnly: true }}
                        sx={{
                            backgroundColor: alpha('#64748b', 0.06),
                            cursor: 'not-allowed',
                            '& input': {
                                cursor: 'not-allowed',
                                color: '#64748b',
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                                WebkitTextFillColor: '#64748b',
                            },
                            '&.Mui-disabled': {
                                opacity: 1,
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Full Name</Label>
                    <StyledInput
                        name="name"
                        value={formData.name || ''}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Designation</Label>
                    <StyledInput
                        name="designation"
                        value={formData.designation || ''}
                        disabled
                        inputProps={{ readOnly: true, 'aria-label': 'Designation (managed in Job Details)' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        Update this from the searchable Designation field in Job Details.
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Date of Birth</Label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={formData.date_of_birth ? dayjs(formData.date_of_birth) : null}
                            onChange={(val) => handleDateChange('date_of_birth', val)}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    sx: {
                                        '& .MuiOutlinedInput-root': {
                                            height: '40px',
                                            borderRadius: '10px',
                                            bgcolor: alpha('#fff', 0.6),
                                            '& fieldset': { border: `1px solid ${alpha(grey[200], 0.5)}` },
                                            '&:hover fieldset': { borderColor: '#3DAC5A' },
                                            '&.Mui-focused fieldset': { borderColor: '#3DAC5A' },
                                        }
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Gender *</Label>
                    <CustomSelect
                        name="gender"
                        value={(formData.gender || '').toLowerCase()}
                        onChange={(e) => handleInputChange({ target: { name: 'gender', value: e.target.value } })}
                        displayEmpty
                    >
                        <MenuItem value="" disabled>Select Gender</MenuItem>
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="others">Others</MenuItem>
                    </CustomSelect>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Region / Location *</Label>
                    <CustomSelect
                        name="location"
                        value={formData.location || ''}
                        onChange={handleInputChange}
                        displayEmpty
                    >
                        {renderSafeOptions(locations, formData.location, 'Select Location')}
                    </CustomSelect>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Nationality</Label>
                    <StyledInput
                        name="nationality"
                        value={formData.nationality || ''}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Mobile Number</Label>
                    <StyledInput
                        name="mobile_number"
                        value={formData.mobile_number || ''}
                        onChange={handleInputChange}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Email Address</Label>
                    <StyledInput
                        name="email"
                        value={formData.email || ''}
                        onChange={handleInputChange}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 5, borderColor: alpha('#64748b', 0.1) }} />

            <Box sx={{ mb: 4 }}>
                <SectionTitle sx={{ mb: 1 }}>
                    <ShareIcon sx={{ color: '#3DAC5A' }} />
                    Social Profiles
                </SectionTitle>
                <Typography variant="body2" color="text.secondary">
                    Professional and social network presence.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <Label>LinkedIn</Label>
                    <StyledInput
                        name="linkedin"
                        value={formData.linkedin || ''}
                        onChange={handleInputChange}
                        placeholder="LinkedIn Profile URL"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Facebook</Label>
                    <StyledInput
                        name="facebook"
                        value={formData.facebook || ''}
                        onChange={handleInputChange}
                        placeholder="Facebook Profile URL"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Instagram</Label>
                    <StyledInput
                        name="instagram"
                        value={formData.instagram || ''}
                        onChange={handleInputChange}
                        placeholder="Instagram Profile URL"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Label>Twitter (X)</Label>
                    <StyledInput
                        name="twitter"
                        value={formData.twitter || ''}
                        onChange={handleInputChange}
                        placeholder="Twitter Profile URL"
                    />
                </Grid>
            </Grid>
        </GlassCard>
    );
};

export default PersonalTab;
